import React, { useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { api, humanBytes, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
    const { user } = useAuth();
    const [form, setForm] = useState({ current: "", next: "", confirm: "" });
    const [submitting, setSubmitting] = useState(false);
    const [show, setShow] = useState(false);

    if (!user) return null;
    const pct = user.storage_quota_bytes
        ? Math.min(100, (user.storage_used_bytes / user.storage_quota_bytes) * 100)
        : 0;

    const submit = async (e) => {
        e.preventDefault();
        if (form.next.length < 6) {
            toast.error("Nieuw wachtwoord moet minstens 6 tekens zijn.");
            return;
        }
        if (form.next !== form.confirm) {
            toast.error("Nieuwe wachtwoorden komen niet overeen.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current,
                new_password: form.next,
            });
            toast.success("Wachtwoord gewijzigd");
            setForm({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[900px]" data-testid="settings-page">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    04 — Account
                </div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-none mb-6 md:mb-8">
                    Instellingen
                </h1>

                <div className="bg-white border border-zinc-200 rounded-md p-4 md:p-6 mb-4 md:mb-6">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
                        Profiel
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <Field label="Naam" value={user.name} />
                        <Field label="E-mail" value={user.email} mono />
                        <Field label="Rol" value={user.role === "admin" ? "Beheerder" : "Klant"} />
                    </div>
                </div>

                <div className="bg-white border border-zinc-200 rounded-md p-4 md:p-6 mb-4 md:mb-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
                        <KeyRound className="w-3 h-3" /> Wachtwoord wijzigen
                    </div>
                    <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
                        <PasswordField
                            label="Huidig wachtwoord"
                            value={form.current}
                            onChange={(v) => setForm({ ...form, current: v })}
                            show={show}
                            testid="current-password"
                        />
                        <PasswordField
                            label="Nieuw wachtwoord"
                            value={form.next}
                            onChange={(v) => setForm({ ...form, next: v })}
                            show={show}
                            hint="Minimum 6 tekens. Gebruik letters, cijfers en symbolen."
                            testid="new-password"
                        />
                        <PasswordField
                            label="Bevestig nieuw wachtwoord"
                            value={form.confirm}
                            onChange={(v) => setForm({ ...form, confirm: v })}
                            show={show}
                            testid="confirm-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShow((s) => !s)}
                            className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900"
                            data-testid="toggle-password-visibility"
                        >
                            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            {show ? "Verberg wachtwoorden" : "Toon wachtwoorden"}
                        </button>
                        <div className="pt-2 border-t border-zinc-100">
                            <Button
                                type="submit"
                                disabled={submitting || !form.current || !form.next || !form.confirm}
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                data-testid="change-password-submit"
                            >
                                {submitting ? "Bezig…" : "Wachtwoord wijzigen"}
                            </Button>
                        </div>
                    </form>
                </div>

                <div className="bg-white border border-zinc-200 rounded-md p-4 md:p-6">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
                        Opslagverbruik
                    </div>
                    <div className="font-display text-3xl md:text-4xl tracking-tighter font-black mb-1">
                        {humanBytes(user.storage_used_bytes)}
                    </div>
                    <div className="text-sm text-zinc-500 mb-4 font-mono">
                        van {humanBytes(user.storage_quota_bytes)} ({pct.toFixed(1)}%)
                    </div>
                    <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full ${pct > 90 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-[#0044FF]"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

function Field({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                {label}
            </div>
            <div className={`text-base ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function PasswordField({ label, value, onChange, show, hint, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                {label}
            </label>
            <Input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="font-mono"
                data-testid={testid}
                autoComplete="new-password"
            />
            {hint && <p className="text-xs text-zinc-500 mt-1.5">{hint}</p>}
        </div>
    );
}
